import { useCallback, useEffect, useRef, useState } from "react"
import { useAppMessage, useDaily, useLocalSessionId } from "./callTransport"
import { CHAT_MESSAGE_TYPE, clampChatText, createSignalId, participantDisplayName, type ChatAppMessage } from "./callSignals"

export type ChatMessage = {
  id: string
  sessionId: string
  name: string
  text: string
  sentAt: number
  local: boolean
}

type UseCallChatArgs = {
  enabled: boolean
  /** Whether the Chat tab is on screen; messages arriving while it is hidden count as unread. */
  isOpen: boolean
  /** Called for every message from someone else (used for chat notifications). */
  onIncoming?: (message: ChatMessage) => void
}

/**
 * Room chat with the same shape as Prebuilt's Chat sidebar: in-memory, app-message based,
 * visible only to people in the call right now, with an unread badge while the tab is hidden.
 */
export function useCallChat({ enabled, isOpen, onIncoming }: UseCallChatArgs) {
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const onIncomingRef = useRef(onIncoming)
  onIncomingRef.current = onIncoming

  useEffect(() => {
    if (isOpen) setUnreadCount(0)
  }, [isOpen])

  const seenIdsRef = useRef<Set<string>>(new Set())
  const handleAppMessage = useCallback((event: { data?: ChatAppMessage; fromId?: string }) => {
    const data = event?.data
    if (!enabledRef.current || data?.type !== CHAT_MESSAGE_TYPE) return
    const text = clampChatText(data.text)
    if (!text) return
    const id = typeof data.id === "string" && data.id ? data.id : createSignalId()
    if (seenIdsRef.current.has(id)) return
    seenIdsRef.current.add(id)
    const message: ChatMessage = {
      id,
      sessionId: event.fromId || "unknown",
      name: participantDisplayName(daily, event.fromId, "Guest"),
      text,
      sentAt: typeof data.sentAt === "number" ? data.sentAt : Date.now(),
      local: false,
    }
    setMessages((current) => [...current, message])
    if (!isOpenRef.current) setUnreadCount((count) => count + 1)
    onIncomingRef.current?.(message)
  }, [daily])
  const sendAppMessage = useAppMessage<ChatAppMessage>({ onAppMessage: handleAppMessage })

  const sendMessage = useCallback((rawText: string) => {
    const text = clampChatText(rawText)
    if (!enabled || !text) return false
    const id = createSignalId()
    const sentAt = Date.now()
    sendAppMessage({ type: CHAT_MESSAGE_TYPE, id, text, sentAt }, "*")
    setMessages((current) => [...current, { id, sessionId: localSessionId, name: "You", text, sentAt, local: true }])
    return true
  }, [enabled, localSessionId, sendAppMessage])

  const markRead = useCallback(() => setUnreadCount(0), [])

  return { messages, unreadCount, sendMessage, markRead }
}
